/**
 * Image attribution display model.
 *
 * Credits are recorded with every photo (Directus fields, data files, data-*
 * attributes) but never render in or next to the content. Each credited image
 * carries its credit as a `title` tooltip, and the page lists its credits once,
 * in a single muted line below the site footer (ImageCreditsFooter).
 *
 * No imports: client components (the grass result view) use this module too.
 */
export interface ImageCredit {
  /** Photographer or rights holder. */
  name: string
  /** Where the photo came from: Unsplash, Pexels, iNaturalist, Wikimedia Commons. */
  source?: string
  /** Link for the name (source page or photographer profile). */
  url?: string
  license?: string
}

const STOCK_SOURCE = /^(pexels|unsplash|pixabay|openverse)$/i

/** Tooltip text for a credited image, e.g. "Photo by Jane Doe on Unsplash". */
export function imageCreditTitle(credit: ImageCredit): string {
  let title = `Photo by ${credit.name}`
  if (credit.source) title += ` on ${credit.source}`
  if (credit.license && !(credit.source && STOCK_SOURCE.test(credit.source))) {
    title += ` (${credit.license})`
  }
  return title
}

/** Human-readable host for a reference-photo sourceUrl. */
export function photoSourceName(sourceUrl: string | null | undefined): string | undefined {
  if (!sourceUrl) return undefined
  if (sourceUrl.includes("inaturalist.org")) return "iNaturalist"
  if (sourceUrl.includes("wikimedia.org") || sourceUrl.includes("wikipedia.org"))
    return "Wikimedia Commons"
  if (sourceUrl.includes("unsplash.com")) return "Unsplash"
  if (sourceUrl.includes("pexels.com")) return "Pexels"
  if (sourceUrl.includes("pixabay.com")) return "Pixabay"
  return undefined
}

/**
 * Build a credit from a reference-photo record ({ credit, license, sourceUrl }).
 * Records without a credit name (CC0, public domain) return null.
 */
export function creditFromPhotoRecord(
  photo:
    | { credit?: string | null; license?: string | null; sourceUrl?: string | null }
    | null
    | undefined
): ImageCredit | null {
  const name = photo?.credit?.trim()
  if (!name) return null
  return {
    name,
    source: photoSourceName(photo?.sourceUrl),
    url: photo?.sourceUrl || undefined,
    license: photo?.license || undefined,
  }
}

/**
 * Parse a free-text credit string: "Photo by Jane Doe on Unsplash",
 * "Photo by Jane Doe, Austin on Pexels", "Photo: Jane Doe / Wikimedia Commons",
 * or a bare name.
 */
export function parseImageCreditText(
  raw: string | null | undefined,
  extra: Omit<ImageCredit, "name"> = {}
): ImageCredit | null {
  const text = raw?.replace(/\s+/g, " ").trim()
  if (!text) return null
  const stock = text.match(/^(?:Photo|Image)\s+by\s+(.+?)\s+on\s+([A-Za-z][\w ]*?)\s*\.?$/i)
  if (stock) {
    const name = stock[1].split(",")[0].trim()
    return name ? { ...extra, name, source: extra.source ?? stock[2] } : null
  }
  const body = text.replace(/^(?:Photo|Image|Illustration)\s*(?::|\s+by\b|\s+from\b)\s*/i, "")
  const [name, source] = body.split(/\s+\/\s+/)
  if (!name?.trim()) return null
  return { ...extra, name: name.trim(), source: extra.source ?? (source?.trim() || undefined) }
}

/** One entry per photographer + source, in first-seen order. */
export function dedupeImageCredits(credits: Array<ImageCredit | null | undefined>): ImageCredit[] {
  const seen = new Set<string>()
  const out: ImageCredit[] = []
  for (const credit of credits) {
    if (!credit?.name) continue
    const key = `${credit.name.toLowerCase()}|${(credit.source ?? "").toLowerCase()}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(credit)
  }
  return out
}
